import { useEffect } from 'react';
import { AuthenticatorType, UpdateAuthenticatorResult } from '@mews-commander/data-access';
import { LocalizedSmallParagraph, useDialogs } from '@mews-commander/ui';
import { FormInputField, LocalizedText, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Banner, Button, Dialog, Link, Stack } from '@optimus-web/core';
import { useCurrentUser } from '@mews-commander/core';
import { use2FAEmailCode } from '../../../../auth/screens/Configure2FA/use2FAEmailCode';
import { useAuthenticatorCodeForm } from './AuthenticatorCodeForm';

export const MFA_DISABLE_DIALOG_NAME = 'disableMFA';
const FORM_ID = 'disable2faForm';

const useDisable2FAForm = (password: string, onSuccess?: (data: UpdateAuthenticatorResult) => void) =>
    useAuthenticatorCodeForm({
        id: FORM_ID,
        onSuccess,
        params: {
            password,
            authenticator: AuthenticatorType.None,
        },
    });

const authenticatorTextKeyMap = {
    [AuthenticatorType.Google]: TextKey.VerifyIdentityInstructions,
    [AuthenticatorType.Email]: TextKey.TwoFactorAuthenticationVerifyEmailDescription,
    [AuthenticatorType.None]: TextKey.FatalErrorMessage,
};

export const MFADisableDialog = ({ password }: { password: string }) => {
    const { closeDialog } = useDialogs();
    const { authenticator } = useCurrentUser();
    const { sendTwoFactorCode } = use2FAEmailCode();

    const handleClose = () => closeDialog(MFA_DISABLE_DIALOG_NAME);
    const handleSuccess = () => {
        handleClose();
        showSuccessAlert({
            title: <LocalizedText textKey={TextKey.TwoFactorAuthenticationDeactivatedTitle} />,
            description: <LocalizedText textKey={TextKey.TwoFactorAuthenticationDeactivatedDescription} />,
        });
    };

    const { renderFormContent, isLoading } = useDisable2FAForm(password, handleSuccess);

    useEffect(() => {
        if (authenticator === AuthenticatorType.Email) {
            sendTwoFactorCode();
        }
    }, [authenticator, sendTwoFactorCode]);

    return (
        <Dialog
            active
            size="small"
            title={<LocalizedText textKey={TextKey.DisableTwoFactorAuthenticationDialogTitle} />}
            loading={isLoading}
            onClose={handleClose}
            footer={{
                primary: (
                    <Button variant="destructive" type="submit" form={FORM_ID}>
                        <LocalizedText textKey={TextKey.Deactivate} />
                    </Button>
                ),
                secondary: (
                    <Button variant="tertiary" type="button" onClick={handleClose}>
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                ),
            }}
        >
            {renderFormContent(
                <Stack vertical spacing="200">
                    <Stack vertical spacing="100">
                        <LocalizedSmallParagraph textKey={authenticatorTextKeyMap[authenticator]} variant="secondary" />
                        {authenticator === AuthenticatorType.Email && (
                            <Link standalone onClick={sendTwoFactorCode} size="small">
                                <LocalizedText textKey={TextKey.ResendEmail} />
                            </Link>
                        )}
                    </Stack>

                    <FormInputField
                        name="pin"
                        size="medium"
                        required
                        label={<LocalizedText textKey={TextKey.AuthenticationCode} />}
                    />

                    <Banner
                        primaryText={<LocalizedText textKey={TextKey.AccountSecurityWillBeDecreased} />}
                        variant="warning"
                    />
                </Stack>
            )}
        </Dialog>
    );
};
